import { useRef, useState } from 'react'
import { notifications } from '@mantine/notifications'
import {
  useEvidenciaDelete,
  useEvidenciaList,
  useEvidenciaUpload,
} from '../../hooks/useEvidencias'
import type { EvidenciaOS } from '../../types'

const MIME_PERMITIDOS = ['image/jpeg', 'image/png', 'image/heic', 'image/webp', 'application/pdf']
const TAMANHO_MAX_MB = 25

interface EvidenciasOSProps {
  ordemServicoId: string
  readonly?: boolean
}

function extractErrorMsg(err: unknown): string {
  const e = err as { response?: { data?: Record<string, unknown> } }
  const data = e?.response?.data ?? {}
  const arquivoErr = data['arquivo']
  if (Array.isArray(arquivoErr) && arquivoErr.length) return String(arquivoErr[0])
  const osErr = data['ordem_servico']
  if (Array.isArray(osErr) && osErr.length) return String(osErr[0])
  if (typeof data['detail'] === 'string') return String(data['detail'])
  return 'Erro ao enviar evidência.'
}

export default function EvidenciasOS({ ordemServicoId, readonly = false }: EvidenciasOSProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [tipo, setTipo] = useState<'foto' | 'documento' | 'laudo' | 'outro'>('foto')
  const [descricao, setDescricao] = useState('')

  const list = useEvidenciaList({ ordem_servico: ordemServicoId })
  const upload = useEvidenciaUpload()
  const del = useEvidenciaDelete()

  function validarArquivo(file: File): string | null {
    if (!MIME_PERMITIDOS.includes(file.type)) {
      return `Tipo "${file.type}" não permitido. Aceitos: JPEG, PNG, HEIC, WebP, PDF.`
    }
    if (file.size > TAMANHO_MAX_MB * 1024 * 1024) {
      return `Arquivo excede limite de ${TAMANHO_MAX_MB}MB.`
    }
    if (file.size <= 0) return 'Arquivo vazio não é aceito.'
    return null
  }

  async function handleUpload(file: File) {
    const erro = validarArquivo(file)
    if (erro) {
      notifications.show({ color: 'red', message: erro })
      return
    }
    try {
      await upload.mutateAsync({
        ordemServicoId,
        arquivo: file,
        tipo,
        descricao: descricao.trim(),
      })
      notifications.show({ color: 'green', message: 'Evidência enviada.' })
      setDescricao('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha no upload',
        message: extractErrorMsg(err),
      })
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) void handleUpload(file)
  }

  async function handleDelete(ev: EvidenciaOS) {
    if (!confirm(`Remover evidência "${ev.descricao || ev.tipo}"?`)) return
    try {
      await del.mutateAsync(ev.id)
      notifications.show({ color: 'green', message: 'Evidência removida.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao remover',
        message: extractErrorMsg(err),
      })
    }
  }

  const items = list.data?.results ?? []

  return (
    <section
      data-testid="evidencias-section"
      style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      <h2 style={{ marginTop: 0 }}>Evidências</h2>

      {!readonly && (
        <div
          data-testid="evidencia-uploader"
          style={{
            marginBottom: '1rem',
            padding: '1rem',
            background: '#F5F5F5',
            borderRadius: '4px',
          }}
        >
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: '120px' }}>
              <label
                htmlFor="evid-tipo"
                style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}
              >
                Tipo
              </label>
              <select
                id="evid-tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as typeof tipo)}
                style={{ padding: '0.4rem', width: '100%' }}
                data-testid="evid-tipo-select"
              >
                <option value="foto">Foto</option>
                <option value="documento">Documento</option>
                <option value="laudo">Laudo</option>
                <option value="outro">Outro</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '180px' }}>
              <label
                htmlFor="evid-descricao"
                style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}
              >
                Descrição (opcional)
              </label>
              <input
                id="evid-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="ex.: foto antes da limpeza"
                style={{ padding: '0.4rem', width: '100%' }}
                data-testid="evid-descricao-input"
              />
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="btn-adicionar-evidencia"
              style={{
                padding: '0.5rem 1rem',
                background: '#1565C0',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              disabled={upload.isPending}
            >
              {upload.isPending ? 'Enviando...' : '+ Adicionar evidência'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              data-testid="input-upload-evidencia"
              accept={MIME_PERMITIDOS.join(',')}
              onChange={onFileChange}
              style={{ display: 'none' }}
            />
          </div>
          <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#666' }}>
            Aceitos: JPEG, PNG, HEIC, WebP, PDF — até {TAMANHO_MAX_MB}MB.
          </p>
        </div>
      )}

      {list.isLoading && <p>Carregando evidências...</p>}

      {!list.isLoading && items.length === 0 && (
        <p style={{ color: '#666', fontStyle: 'italic' }}>Nenhuma evidência anexada ainda.</p>
      )}

      {items.length > 0 && (
        <div
          data-testid="evidencias-lista"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '0.75rem',
          }}
        >
          {items.map((ev) => (
            <div
              key={ev.id}
              data-testid={`evidencia-${ev.id}`}
              style={{
                border: '1px solid #E0E0E0',
                borderRadius: '4px',
                padding: '0.5rem',
                background: 'white',
              }}
            >
              {ev.mime_type.startsWith('image/') && ev.arquivo_url ? (
                <a href={ev.arquivo_url} target="_blank" rel="noreferrer">
                  <img
                    src={ev.arquivo_url}
                    alt={ev.descricao || ev.tipo}
                    style={{
                      width: '100%',
                      height: '120px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                    }}
                  />
                </a>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '120px',
                    background: '#FAFAFA',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                  }}
                >
                  📄 {ev.tipo_display ?? ev.tipo}
                </div>
              )}
              <p
                style={{
                  margin: '0.5rem 0 0',
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={ev.descricao}
              >
                {ev.descricao || ev.tipo_display || ev.tipo}
              </p>
              <p style={{ margin: 0, fontSize: '0.7rem', color: '#888' }}>
                {(ev.tamanho_bytes / 1024).toFixed(1)} KB
              </p>
              {!readonly && (
                <button
                  type="button"
                  onClick={() => handleDelete(ev)}
                  data-testid={`btn-remover-evidencia-${ev.id}`}
                  style={{
                    marginTop: '0.25rem',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    background: 'none',
                    color: '#C62828',
                    border: '1px solid #C62828',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Remover
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
